import { Field, FieldProps, Form, Formik } from "formik";

import { Specimen, SpecimenPATCH } from "pwapi";

import { updateSpecimenDetails } from "shared/actions/caseDetailActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { procedureEnumToLabel } from "shared/utils/specimenUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import SelectInput from "components/customInputs/SelectInput";
import TextareaInput from "components/customInputs/TextareaInput";
import TextInput from "components/customInputs/TextInput";
import Spinner from "components/Spinner";
import { useAppDispatch } from "store/hooks";

import SidebarFooter from "./SidebarFooter";
import { SpecimenDialogProps } from "./SpecimenDialogController";

const getInitialValues = (
  initialValues: { [key: string]: string },
  specimen: Specimen
) => {
  return Object.keys(initialValues).reduce(
    (acc: { [key: string]: string }, key) => {
      acc[key] = specimen[key as keyof Specimen] || "";
      return acc;
    },
    {}
  );
};

const SpecimenDialogEditForm = ({
  specimen,
  onClose,
}: SpecimenDialogProps & { onClose: Function }) => {
  const dispatch = useAppDispatch();
  const isLoading = useIsLoading(updateSpecimenDetails, { id: specimen?.id });
  const initialValues = getInitialValues(
    {
      clinical_history: "",
      gross_description: "",
      site: "",
      procedure: "",
      type: "",
    },
    specimen
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => {
        const submitForm = Object.keys(values).reduce(
          (acc: { [key: string]: any }, key) => {
            return {
              ...acc,
              ...(values[key] !== initialValues[key] && { [key]: values[key] }),
            };
          },
          { id: specimen.id }
        ) as SpecimenPATCH;
        if (specimen?.id && !isLoading) {
          dispatch(
            updateSpecimenDetails({ id: specimen.id, requestBody: submitForm })
          ).then(() => {
            onClose();
          });
        }
      }}
    >
      {({ dirty }) => (
        <Form>
          <Field name="clinical_history">
            {({ field }: FieldProps) => (
              <TextareaInput
                label="Clinical History"
                id="clinical_history"
                rows={4}
                {...field}
              />
            )}
          </Field>
          <div className="Sidebar__inputGroup">
            <Field name="type">
              {({ field }: FieldProps) => (
                <SelectInput
                  label="Type"
                  id="type"
                  options={Object.values(Specimen.type).map((a) => ({
                    label: a,
                    value: a,
                  }))}
                  {...field}
                />
              )}
            </Field>
            <Field name="site">
              {({ field }: FieldProps) => (
                <TextInput label="Site" id="site" {...field} />
              )}
            </Field>
            <Field name="procedure">
              {({ field }: FieldProps) => (
                <SelectInput
                  label="Procedure"
                  id="procedure"
                  options={Object.values(Specimen.procedure)
                    .filter(
                      (v) =>
                        specimen.procedure === Specimen.procedure.U ||
                        v !== Specimen.procedure.U
                    )
                    .map((a) => ({
                      label: procedureEnumToLabel(a) || "",
                      value: a,
                      disabled: a === Specimen.procedure.U,
                    }))}
                  {...field}
                />
              )}
            </Field>
          </div>
          <Field name="gross_description">
            {({ field }: FieldProps) => (
              <TextareaInput
                label="Gross Description"
                id="gross_description"
                rows={6}
                {...field}
              />
            )}
          </Field>

          <SidebarFooter hasTopBorder={false}>
            <div className="SidebarFooter__actionsContainer">
              <PathologyWatchButton
                type="button"
                color="red"
                onClick={() => onClose()}
              >
                Cancel
              </PathologyWatchButton>
              <PathologyWatchButton
                type="submit"
                disabled={!dirty || isLoading}
              >
                {isLoading ? (
                  <span className="Sidebar__spinnerButton">
                    <Spinner size="20px" /> Processing
                  </span>
                ) : (
                  <>Save Changes</>
                )}
              </PathologyWatchButton>
            </div>
          </SidebarFooter>
        </Form>
      )}
    </Formik>
  );
};

export default SpecimenDialogEditForm;
